import { hasRenderableLocalDocumentPreview } from '../services/document-blob-store';
import React from 'react';
import { classifyDocumentType } from '../services/document-type-classifier';

// Generation-agnostic version match for Gemini so future releases (3.x, 4.x,
// etc.) aren't rejected as "not vision-capable" by a stale allow-list. Every
// Gemini 1.5+ model on OpenRouter takes image input. Peter 2026-04-21: Gemini
// 3.1 Flash Lite was being rejected here and at PhotoMarkup.
export const VISION_CAPABLE_PATTERNS = /gpt-4o|gpt-4-turbo|gpt-5|gemini-[1-9]|gemini.*vision|claude.*sonnet|claude.*opus|claude.*haiku|llava|pixtral|qwen.*vl|llama.*vision/i;
export const isVisionCapable = (modelName?: string | null): boolean => !!modelName && VISION_CAPABLE_PATTERNS.test(modelName);

export const DOCX_MIME_WO_E = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

// Decide whether a file is previewable. Honours the qm_disable_docx_preview
// localStorage kill switch (DOCX only; PDF preview stays available).
export function canPreviewFile(contentType?: string | null): boolean {
    if (!contentType) return false;
    const mime = String(contentType).toLowerCase();
    if (mime === 'application/pdf') return true;
    if (mime.startsWith('image/')) return true;
    if (mime === DOCX_MIME_WO_E) {
        try {
            if (typeof localStorage !== 'undefined' && localStorage.getItem('qm_disable_docx_preview') === 'true') return false;
        } catch { /* sandbox */ }
        return true;
    }
    return false;
}

export const STAGE1_IMAGE_EXT_RE = /^(jpg|jpeg|jfif|png|gif|webp|heic|heif|bmp|tif|tiff)$/i;
export const STAGE1_IMAGE_FILENAME_RE = /\.(jpg|jpeg|jfif|png|gif|webp|heic|heif|bmp|tif|tiff)$/i;

export function inferDocumentType(filename: string, content?: string): string {
    const result = classifyDocumentType(filename, content || '');
    return result.class;
}

export function classifyPlainTextFileForRouting(filename: string, content: string = ''): { classification: any; shouldRouteAsDocument: boolean } {
    const classification = classifyDocumentType(filename, content);
    return {
        classification,
        shouldRouteAsDocument: classification.class === 'rfq'
            // classifyDocumentType never returns this class today; kept as is.
            || (classification.class as string) === 'scope_of_works',
    };
}

export const PLAN_VISION_PROMPT = `You are a senior construction estimator analysing architectural/engineering plan drawings for a building project quote.

Examine every page of this plan document carefully and extract ALL scope-relevant information:

## STRUCTURAL & ARCHITECTURAL
- Building type, storeys, overall dimensions
- Room/area breakdown with approximate dimensions (length × width)
- Wall types (brick, timber frame, steel, concrete block, party wall)
- Roof type and material (tile, metal, flat, pitched - include pitch angle if shown)
- Floor construction (slab on ground, suspended, timber)
- Foundation/footing details

## FINISHES & MATERIALS
- Internal wall finishes (plaster, render, tile)
- Floor finishes per room (tile, timber, carpet, vinyl)
- Ceiling types and heights
- External cladding/render
- Window and door types, sizes, materials
- Balustrades, handrails

## SERVICES & FITTINGS
- Bathroom/kitchen/laundry fixture counts
- Plumbing rough-in locations
- Electrical switchboard, points, lighting
- Mechanical (AC, ventilation) if shown
- Stormwater, drainage, sewer connections

## SITE & CONTEXT
- Site boundaries, setbacks, easements
- Demolition scope (if renovation/alteration)
- Existing structure vs new work
- Access constraints, retaining walls
- Landscaping, paving, fencing shown on plans

## ANNOTATIONS & NOTES
- ALL text annotations, callouts, and revision notes on the drawings
- Title block info (drawing number, revision, date, scale, author)
- Referenced Australian Standards or BCA requirements
- Any specification notes or legend items

Return a structured, detailed analysis. Use headings. Include measurements where visible.
If a page is a cover sheet or title page with no drawings, summarise its content briefly.
If dimensions or details are unclear, note them as approximate with [approx.] tag.`;

export const PDF_LAYOUT_VISION_PROMPT = `You are reading rendered pages from a construction reference PDF whose selectable-text layer has a complex layout. The supplied images are ordered by the explicit page-number list below.

Read each page visually, preserving the intended reading order of columns, tables, callouts, legends, annotations and captions. Do not summarise away measurements, moisture readings, defects, scope instructions, exclusions, names, dates or reference numbers. Do not invent content that is not visible.

Return plain text with one section per image in exactly this form:
--- Page <page number> (layout-aware visual reading) ---
<faithful structured reading using short headings, rows or bullets where appropriate>

PAGE NUMBERS: {PAGE_NUMBERS}`;

export function captureVideoThumbnail(videoUrl: string, { width = 240, quality = 0.6, seekTo = 0.5 } = {}): Promise<string> {
    return new Promise((resolve, reject) => {
        if (!videoUrl) { reject(new Error('no url')); return; }
        const vid = document.createElement('video');
        vid.muted = true;
        vid.playsInline = true;
        vid.preload = 'auto';
        if (typeof videoUrl === 'string' && !videoUrl.startsWith('blob:') && !videoUrl.startsWith('data:')) {
            vid.crossOrigin = 'anonymous';
        }

        let done = false;
        const cleanup = () => {
            done = true;
            try { vid.pause(); } catch {}
            vid.src = '';
            try { vid.removeAttribute('src'); vid.load(); } catch {}
        };
        const fail = (msg: string) => { if (done) return; cleanup(); reject(new Error(msg)); };
        const timer = setTimeout(() => fail('thumbnail timeout'), 8000);

        vid.onloadedmetadata = () => {
            const targetT = Number.isFinite(vid.duration) && vid.duration > 0
                ? Math.min(seekTo, Math.max(0, vid.duration - 0.05))
                : seekTo;
            try { vid.currentTime = targetT; } catch { fail('seek failed'); }
        };
        vid.onseeked = () => {
            try {
                const w = vid.videoWidth || width;
                const h = vid.videoHeight || Math.round(width * 0.5625);
                const scale = width / w;
                const canvas = document.createElement('canvas');
                canvas.width = Math.round(w * scale);
                canvas.height = Math.round(h * scale);
                const ctx = canvas.getContext('2d');
                if (!ctx) { fail('no 2d ctx'); return; }
                ctx.drawImage(vid, 0, 0, canvas.width, canvas.height);
                const dataUrl = canvas.toDataURL('image/jpeg', quality);
                clearTimeout(timer);
                cleanup();
                resolve(dataUrl);
            } catch (err: any) {
                fail(err?.message || 'draw failed');
            }
        };
        vid.onerror = () => fail('video element error');

        vid.src = videoUrl;
    });
}

export function showPipelineEvidencePanels(): boolean {
    try { return typeof localStorage !== 'undefined' && localStorage.getItem('qm_stage1_show_pipeline_evidence') === 'true'; }
    catch { return false; }
}

export function getControlledDocumentPreview(doc: any) {
    const name = doc?.name || doc?.originalName || 'Document';
    if (hasRenderableLocalDocumentPreview(doc)) return null;
    const previewId = doc?.fileMetadataId || doc?.pdfS3Key;
    const previewMime = doc?.contentType || doc?.mimeType
        || (String(doc?.ext || '').toLowerCase() === 'pdf' || String(name).toLowerCase().endsWith('.pdf') ? 'application/pdf' : null);
    if (!previewId || !canPreviewFile(previewMime)) return null;
    return { fileId: previewId, contentType: previewMime, title: name };
}
